import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useCandidate, useCandidateAssignments } from '@/hooks/use-candidates';
import { isConsentLocked } from '../lib/consent-actions';
import { cn } from '@/lib/utils';
import type { AssignmentEmployeeRef, CandidateAssignment } from '@/types/candidates';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  ASSIGNMENT_RESULT_LABEL,
  ASSIGNMENT_SOURCE_LABEL,
} from '../lib/assignment-labels';
import {
  AssignCandidateDialog,
  type AssignableCandidate,
} from './assign-candidate-dialog';

interface CandidateAssignmentsTableProps {
  candidateId: string;
}

export function CandidateAssignmentsTable({ candidateId }: CandidateAssignmentsTableProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const { data: assignments = [], isLoading, error } = useCandidateAssignments(candidateId);
  const { data: candidate } = useCandidate(candidateId);

  // HW-1055: a candidate who revoked consent is not added to vacancies.
  const assignable: AssignableCandidate | null =
    candidate && !isConsentLocked(candidate)
      ? {
          id: candidate.id,
          fullName: candidate.fullName,
          activeAssignments: assignments
            .filter((a) => a.result === 'IN_PROGRESS')
            .map((a) => ({ vacancyId: a.vacancyId })),
        }
      : null;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium">Vacancy assignments</h2>
          <p className="text-sm text-muted-foreground">
            All vacancies this candidate is on — active and archived.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => setAssignOpen(true)}
          disabled={!assignable}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Assign to vacancy
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full">
          <thead className="border-b bg-muted/50">
            <tr>
              <TH>Vacancy</TH>
              <TH>Recruiter</TH>
              <TH>Stage</TH>
              <TH>Source</TH>
              <TH>Result</TH>
              <TH className="w-[1%] whitespace-nowrap text-right" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-destructive">
                  Failed to load assignments.
                </td>
              </tr>
            ) : assignments.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <p className="text-sm text-muted-foreground">
                    This candidate isn't assigned to any vacancies yet.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setAssignOpen(true)}
                    disabled={!assignable}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Assign to vacancy
                  </Button>
                </td>
              </tr>
            ) : (
              assignments.map((a) => <AssignmentRow key={a.id} assignment={a} />)
            )}
          </tbody>
        </table>
      </div>

      <AssignCandidateDialog
        candidate={assignOpen ? assignable : null}
        open={assignOpen}
        onOpenChange={setAssignOpen}
      />
    </section>
  );
}

function TH({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-4 py-3 text-left text-sm font-medium text-muted-foreground',
        className
      )}
    >
      {children}
    </th>
  );
}

function AssignmentRow({ assignment }: { assignment: CandidateAssignment }) {
  const recruiter = assignment.vacancyCreatedBy ?? assignment.assignedBy;
  const subtextParts: string[] = [];
  if (assignment.vacancyDepartment?.name) subtextParts.push(assignment.vacancyDepartment.name);
  if (assignment.vacancyLocation?.name) subtextParts.push(assignment.vacancyLocation.name);

  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/30">
      <td className="px-4 py-3">
        <Link
          to="/recruitment/vacancies/$id"
          params={{ id: assignment.vacancyId }}
          className="inline-block max-w-xs truncate align-bottom text-sm font-medium text-foreground hover:text-hygge hover:underline"
          title={assignment.vacancyTitle}
        >
          {assignment.vacancyTitle}
        </Link>
        {subtextParts.length > 0 ? (
          <div
            className="mt-0.5 max-w-xs truncate text-xs text-muted-foreground"
            title={subtextParts.join(' · ')}
          >
            {subtextParts.join(' · ')}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        <RecruiterCell recruiter={recruiter} />
      </td>
      <td className="px-4 py-3">
        <span
          className="block max-w-xs truncate text-sm text-foreground"
          title={assignment.currentStageName ?? undefined}
        >
          {assignment.currentStageName ?? '—'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {ASSIGNMENT_SOURCE_LABEL[assignment.assignmentSource]}
      </td>
      <td className="px-4 py-3">
        <ResultPill assignment={assignment} />
      </td>
      <td className="w-[1%] whitespace-nowrap px-4 py-3 text-right">
        <Button variant="ghost" size="sm" asChild>
          <Link
            to="/recruitment/vacancies/$id"
            params={{ id: assignment.vacancyId }}
          >
            Open
          </Link>
        </Button>
      </td>
    </tr>
  );
}

function RecruiterCell({ recruiter }: { recruiter: AssignmentEmployeeRef | null }) {
  if (!recruiter) return <span className="text-sm text-muted-foreground">—</span>;
  const name = `${recruiter.firstName} ${recruiter.lastName}`.trim();
  const initials =
    `${recruiter.firstName[0] ?? ''}${recruiter.lastName[0] ?? ''}`.toUpperCase() || '?';
  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-6 w-6 rounded-full">
        {recruiter.avatarUrl ? (
          <AvatarImage src={recruiter.avatarUrl} alt={name} />
        ) : null}
        <AvatarFallback className="rounded-full text-[10px]">{initials}</AvatarFallback>
      </Avatar>
      <span className="max-w-[200px] truncate text-sm text-foreground" title={name}>
        {name}
      </span>
    </div>
  );
}

function ResultPill({ assignment }: { assignment: CandidateAssignment }) {
  const label = ASSIGNMENT_RESULT_LABEL[assignment.result];
  const base = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

  if (assignment.result === 'HIRED') {
    return (
      <span className={cn(base, 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400')}>
        {label}
      </span>
    );
  }
  if (assignment.result === 'REJECTED') {
    return (
      <div className="space-y-1">
        <span className={cn(base, 'bg-destructive/15 text-destructive')}>{label}</span>
        {assignment.rejectionReason ? (
          <p
            className="max-w-[200px] truncate text-xs text-muted-foreground"
            title={assignment.rejectionReason.label}
          >
            {assignment.rejectionReason.label}
          </p>
        ) : null}
        {assignment.rejectionComment ? (
          <p
            className="line-clamp-2 max-w-[200px] wrap-anywhere text-xs text-muted-foreground/70"
            title={assignment.rejectionComment}
          >
            {assignment.rejectionComment}
          </p>
        ) : null}
      </div>
    );
  }
  return <span className={cn(base, 'bg-muted text-muted-foreground')}>{label}</span>;
}
